// @ts-strict-ignore
import { FCHttpParamEncoder, MomentService } from '@fc-core';
import { BaseApiService } from '../core/services/base-api.service';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import moment, { Moment } from 'moment';
import { map } from 'rxjs/operators';
import {
  IftaReport,
  VehicleOdometer,
  VehicleStatesReport,
  VehicleUsage,
} from './models/reports.model';
import { GroupReport } from './store/reducers/ifta.reducer';
import { VehicleWorkHoursReport } from './store/reducers/vehicle-work-hours.reducer';
import { ScheduleReport } from '../store/reducers/scheduled-reports.reducer';
import { UnresponsiveReport } from './store/reducers/unresponsive-report.reducer';
import { ReportsPeriod } from './models/reports.period';
import { MaintenanceReportResponse } from './models/maintenance-report.response';
import { LocationEngineHoursByLocationReport } from './models/location-engine-hours-by-location-report';
import { LocationReport } from './store/reducers/location-report.reducer';

import { EmployeeWorkHoursReport } from './models/employee-work-hours.model';
import { VehicleMilesByLocationReport } from './models/location-vehicle-miles-report';
import { DATE_TIME_FORMATS } from '@fc-shared/utils/date-time-formats';

export interface ReportRow {
  enterOdometer: number;
  entryDatetime: string;
  exitDatetime: string;
  exitOdometer: number;
  miles: number;
  state: string;
}

@Injectable({ providedIn: 'root' })
export class ReportsService extends BaseApiService {
  // todo move to store
  selectedLocation: number;
  selectedGroup: number;
  selectedVehicle: number;
  selectedLocationType: number;
  selectedEmployee: string;

  constructor(
    private http: HttpClient,
    private momentService: MomentService,
  ) {
    super();
  }

  clearSelected() {
    this.selectedLocation = null;
    this.selectedGroup = null;
    this.selectedVehicle = null;
    this.selectedLocationType = null;
    this.selectedEmployee = null;
  }

  getGroupReports(
    groupId: number,
    start: Moment,
    end: Moment,
  ): Observable<GroupReport> {
    const params = this.getReportsParams(
      start.toISOString(),
      end.toISOString(),
    );
    return this.http.get<GroupReport>(
      `${this.getBaseUrl()}/reports/state_mileage/group/${groupId}/`,
      { params },
    );
  }

  public getIftaReport(
    id: number,
    start: string,
    end: string,
  ): Observable<{ rows: IftaReport[]; summary: any }> {
    const params = this.getReportsParams(start, end);

    return this.http
      .get<{ rows: ReportRow[]; summary: any; meta: any }>(
        `${this.getBaseUrl()}/reports/state_mileage/vehicle/${id}/`,
        {
          params,
        },
      )
      .pipe(
        map((res: { rows: ReportRow[]; summary: any; meta: any }) => {
          return {
            rows: res.rows.map((item) => ({
              enterOdometer: item.enterOdometer,
              enteredStateAt: this.momentService
                .moment(item.entryDatetime)
                .format(DATE_TIME_FORMATS.displayTimestamp),
              exitOdometer: item.exitOdometer,
              exitedStateAt: this.momentService
                .moment(item.exitDatetime)
                .format(DATE_TIME_FORMATS.displayTimestamp),
              miles: item.miles,
              state: item.state,
              isValid:
                moment(item.entryDatetime).isValid() &&
                moment(item.exitDatetime).isValid(),
            })),
            summary: res.summary,
          };
        }),
      );
  }

  public getVehicleUsageReport(
    start: string,
    end: string,
  ): Observable<{
    vehicleUsage: VehicleUsage[];
    statistics: {
      averageRatio: number;
      totalIdlingSeconds: number;
      totalStoppedSeconds: number;
      totalTravelingSeconds: number;
    };
  }> {
    const params = this.getReportsParams(start, end);

    return this.http
      .get(`${this.getBaseUrl()}/reports/company/vehicle/usage/`, {
        params,
      })
      .pipe(
        map((res: any) => {
          return {
            vehicleUsage: res.vehicles.map((item: any) => ({
              identifier: item.identifier,
              totalIdlingSeconds: item.totalIdlingSeconds,
              totalTravelingSeconds: item.totalTravelingSeconds,
              totalStoppedSeconds: item.totalStoppedSeconds,
              usageRatio: +(item.usageRatio * 100).toFixed(1),
              reportingFrom: this.momentService
                .moment(item.reportingFrom)
                .format(DATE_TIME_FORMATS.displayTimestamp),
              reportingTo: this.momentService
                .moment(item.reportingTo)
                .format(DATE_TIME_FORMATS.displayTimestamp),
              isValid:
                moment(item.reportingFrom).isValid() &&
                moment(item.reportingTo).isValid(),
              isBeforeCurrent: moment(item.reportingTo).isBefore(
                moment(end).format(),
                'day',
              ),
            })),
            statistics: {
              averageRatio: res.averageRatio,
              totalIdlingSeconds: res.totalIdlingSeconds,
              totalStoppedSeconds: res.totalStoppedSeconds,
              totalTravelingSeconds: res.totalTravelingSeconds,
            },
          };
        }),
      );
  }

  public getVehicleOdometerReport(
    vehicleId: number,
    groupId: number,
  ): Observable<{
    odometers: VehicleOdometer[];
    statistics: {
      vehicles: number;
      minOdometer: number;
      maxOdometer: number;
      avgOdometer: number;
    };
  }> {
    let params = new HttpParams();
    if (vehicleId) params = params.set('vehicle_id', vehicleId);
    if (groupId) params = params.set('group_id', groupId.toString());
    return this.http
      .get(`${this.getBaseUrl()}/reports/company/vehicle/odometer/`, {
        params,
      })
      .pipe(
        map((res: any) => {
          return {
            odometers: res.odometers.map((item: any) => ({
              identifier: item.identifier,
              manufacturer: item.manufacturer,
              model: item.model,
              year: item.year,
              odometer: item.odometer,
            })),
            statistics: {
              minOdometer: res.minOdometer,
              maxOdometer: res.maxOdometer,
              avgOdometer: res.avgOdometer,
              vehicles: res.vehicles,
            },
          };
        }),
      );
  }

  public getVehicleWorkHoursReport(
    id: number,
    start: string,
    end: string,
  ): Observable<VehicleWorkHoursReport> {
    const params = this.getReportsParams(start, end);

    return this.http
      .get(`${this.getBaseUrl()}/reports/company/work-hours/vehicle/${id}/`, {
        params,
      })
      .pipe(
        map((res: any) => {
          return {
            rows: res.rows.map((item: any) => ({
              date: this.momentService.moment(item.date),
              vehicleId: item.vehicleId,
              vehicleIdentifier: item.vehicleIdentifier,
              startTime: this.getWorkHour(item.startTime),
              endTime: this.getWorkHour(item.endTime),
              hoursWorked: item.hoursWorked,
              states: item.states,
            })),
            summary: res.summary,
            meta: null as any,
          };
        }),
      );
  }

  public getVehicleWorkHoursReportForGroup(
    id: number,
    start: string,
    end: string,
  ): Observable<VehicleWorkHoursReport> {
    const params = this.getReportsParams(start, end);

    return this.http
      .get(`${this.getBaseUrl()}/reports/company/work-hours/group/${id}/`, {
        params,
      })
      .pipe(
        map((res: any) => {
          return {
            rows: res.rows.map((item: any) => ({
              date: this.momentService.moment(item.date),
              vehicleId: item.vehicleId,
              vehicleIdentifier: item.vehicleIdentifier,
              startTime: this.getWorkHour(item.startTime),
              endTime: this.getWorkHour(item.endTime),
              hoursWorked: item.hoursWorked,
              states: item.states,
            })),
            summary: res.summary,
            meta: null as any,
          };
        }),
      );
  }

  getScheduleReports(): Observable<ScheduleReport[]> {
    return this.http.get<ScheduleReport[]>(
      `${this.getBaseUrl()}/reports/scheduled/`,
    );
  }

  private getWorkHour(time: string) {
    const t: Moment = this.momentService.moment(time);
    t.set({ year: 1, month: 1, date: 1 });

    if (t.isValid()) {
      return t;
    } else {
      return null;
    }
  }

  deleteScheduleReports(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.getBaseUrl()}/reports/scheduled/${id}/`,
    );
  }

  updateScheduleReport(schedule: ScheduleReport): Observable<ScheduleReport> {
    return this.http.put<ScheduleReport>(
      `${this.getBaseUrl()}/reports/scheduled/${schedule.id}/`,
      schedule,
    );
  }

  addScheduleReports(
    scheduleReport: ScheduleReport,
  ): Observable<ScheduleReport> {
    return this.http.post<ScheduleReport>(
      `${this.getBaseUrl()}/reports/scheduled/`,
      scheduleReport,
    );
  }

  getMaintenanceReport(
    vehicleId: number,
    groupId: number,
    start: string,
    end: string,
  ): Observable<MaintenanceReportResponse> {
    const params = this.getReportsParams(start, end);
    return this.http.get<MaintenanceReportResponse>(
      `${this.getBaseUrl()}/reports/maintenance/${
        vehicleId ? 'vehicle' : 'group'
      }/${vehicleId ? vehicleId : groupId}/`,
      {
        params,
      },
    );
  }

  getLocationBasedReportsParams(
    locationId: number,
    groupId: number,
    vehicleId: number,
    locationType: number,
    start: string,
    end: string,
  ): HttpParams {
    let params = this.getReportsParams(start, end);
    if (locationId) params = params.set('location_id', locationId.toString());
    if (vehicleId) params = params.set('vehicle_id', vehicleId.toString());
    if (groupId) params = params.set('group_id', groupId.toString());
    if (locationType)
      params = params.set('location_type', locationType.toString());

    return params;
  }

  getLocationReportsByLocation(
    locationId: number,
    groupId: number,
    vehicleId: number,
    locationType: number,
    start: string,
    end: string,
  ): Observable<LocationReport[]> {
    const params = this.getLocationBasedReportsParams(
      locationId,
      groupId,
      vehicleId,
      locationType,
      start,
      end,
    );

    return this.http
      .get(`${this.getBaseUrl()}/reports/location`, {
        params,
      })
      .pipe(map((data: any) => data['rows']));
  }

  getEnginHoursReportsByLocation(
    locationId: number,
    groupId: number,
    vehicleId: number,
    locationType: number,
    start: string,
    end: string,
  ): Observable<LocationEngineHoursByLocationReport> {
    const params = this.getLocationBasedReportsParams(
      locationId,
      groupId,
      vehicleId,
      locationType,
      start,
      end,
    );
    return this.http.get(
      `${this.getBaseUrl()}/reports/engine-hours-by-location/`,
      {
        params,
      },
    );
  }

  getLocationTotalTime(
    locationId: number,
    groupId: number,
    vehicleId: number,
    locationType: number,
    start: string,
    end: string,
  ): Observable<LocationEngineHoursByLocationReport> {
    const params = this.getLocationBasedReportsParams(
      locationId,
      groupId,
      vehicleId,
      locationType,
      start,
      end,
    );
    return this.http.get(`${this.getBaseUrl()}/reports/total-time-location/`, {
      params,
    });
  }

  getVehicleMilesByLocation(
    locationId: number,
    groupId: number,
    vehicleId: number,
    locationType: number,
    start: string,
    end: string,
  ): Observable<VehicleMilesByLocationReport> {
    const params = this.getLocationBasedReportsParams(
      locationId,
      groupId,
      vehicleId,
      locationType,
      start,
      end,
    );
    return this.http.get(`${this.getBaseUrl()}/reports/miles-by-location/`, {
      params,
    });
  }

  getUnresponsiveReport(
    start: string,
    end: string,
  ): Observable<UnresponsiveReport[]> {
    const s = moment(start);
    const e = moment(end);
    const dates = e.diff(s, 'days');
    return this.http
      .get(
        `${this.getBaseUrl()}/reports/unresponsive-vehicles/?unresponsive_for_last=${dates}`,
      )
      .pipe(map((data: any) => data.vehicles));
  }

  public loadStatesReports(
    id: number,
    start: string,
    end: string,
    perPeriod: ReportsPeriod,
  ): Observable<VehicleStatesReport> {
    const params = this.getReportsParams(start, end, false, perPeriod);
    return this.http.get<VehicleStatesReport>(
      `${this.getBaseUrl()}/reports/company/vehicle-states/vehicle/${id}/`,
      {
        params,
      },
    );
  }

  public loadStatesReportsForGroup(
    id: number,
    start: string,
    end: string,
  ): Observable<VehicleStatesReport> {
    const params = this.getReportsParams(start, end);
    return this.http.get<VehicleStatesReport>(
      `${this.getBaseUrl()}/reports/company/vehicle-states/group/${id}/`,
      {
        params,
      },
    );
  }

  public loadEmployeeWorkHoursReport(
    employeeId: string,
    groupId: number,
    start: string,
    end: string,
  ): Observable<EmployeeWorkHoursReport> {
    let params = new HttpParams({ encoder: new FCHttpParamEncoder() })
      .set('lower', this.momentService.moment(start).format())
      .set('upper', this.momentService.moment(end).format());
    if (employeeId) params = params.set('employee_id', employeeId);
    if (groupId) params = params.set('group_id', groupId.toString());
    return this.http.get<EmployeeWorkHoursReport>(
      `${this.getBaseUrl()}/reports/employee-work-hours/`,
      {
        params,
      },
    );
  }

  private getReportsParams(
    start: string,
    end: string,
    csv = false,
    period?: ReportsPeriod,
  ): HttpParams {
    let params = new HttpParams({ encoder: new FCHttpParamEncoder() });

    if (!params.has(start)) {
      params = params.set('lower', this.momentService.moment(start).format());
    }

    if (!params.has(end)) {
      params = params.set('upper', this.momentService.moment(end).format());
    }

    if (csv) {
      params = params.set('format', 'csv');
    }

    if (period) {
      params = params.set('period_type', period);
    }

    return params;
  }
}
